/**
 * Beta Feedback Page
 *
 * Allows beta users to submit feedback about their experience.
 * Uses the existing submitFeedbackHttp Cloud Function.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  MessageSquare,
  Eye,
  FolderOpen,
  BookOpen,
  FileEdit,
  MessageCircle,
  Bug,
  Lightbulb,
  Puzzle,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import { Sidebar } from '../components/dashboard/Sidebar';
import { Header } from '../components/dashboard/Header';
import { useCaseFile } from '../contexts/CaseFileContext';

// Module options for feedback area selection - using same icons as dashboard
const modules = [
  { id: 'watchtower', name: 'Watchtower', icon: Eye, desc: 'Deadline monitoring', color: 'text-amber-600 bg-amber-50' },
  { id: 'casefile', name: 'CaseFile', icon: FolderOpen, desc: 'Document management', color: 'text-evergreen bg-evergreen/10' },
  { id: 'carelog', name: 'CareLog', icon: BookOpen, desc: 'Daily tracking', color: 'text-blue-600 bg-blue-50' },
  { id: 'casemaker', name: 'CaseMaker', icon: FileEdit, desc: 'Letter generation', color: 'text-purple-600 bg-purple-50' },
  { id: 'general', name: 'General', icon: MessageCircle, desc: 'Overall experience', color: 'text-slate-600 bg-slate-100' },
];

// Feedback type options - mapped to backend types
const feedbackTypes = [
  { id: 'bug', label: 'Bug Report', icon: Bug, backendType: 'bug' },
  { id: 'feature_request', label: 'Feature Request', icon: Lightbulb, backendType: 'feature_request' },
  { id: 'usability', label: 'Usability Issue', icon: Puzzle, backendType: 'usability' },
  { id: 'general', label: 'Question', icon: HelpCircle, backendType: 'general' },
  { id: 'praise', label: "What's Working", icon: Sparkles, backendType: 'praise' },
];

// Priority options for bugs/usability issues
const priorities = [
  { id: 'low', label: 'Minor', desc: 'Nice to fix eventually' },
  { id: 'medium', label: 'Moderate', desc: 'Impacts my workflow' },
  { id: 'high', label: 'Significant', desc: 'Blocking important tasks' },
  { id: 'critical', label: 'Critical', desc: "Can't use the feature at all" },
];

interface SubmitFeedbackRequest {
  type: string;
  title: string;
  content: string;
  pageUrl?: string;
  featureArea?: string;
  rating?: number;
  userAgent?: string;
  appVersion?: string;
}

interface SubmitFeedbackResponse {
  success: boolean;
  feedbackId?: string;
  message?: string;
  error?: string;
}

export function BetaFeedbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { activeCaseFileId } = useCaseFile();

  // Form state
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [wantsFollowup, setWantsFollowup] = useState(false);

  // UI state
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const submitFeedback = httpsCallable<SubmitFeedbackRequest, SubmitFeedbackResponse>(
        functions,
        'submitFeedbackHttp'
      );

      // Find the backend type for the selected feedback type
      const selectedType = feedbackTypes.find(t => t.id === feedbackType);

      // Build a title from the module and type
      const moduleLabel = modules.find(m => m.id === selectedModule)?.name || 'General';
      const typeLabel = selectedType?.label || 'Feedback';
      const title = `[${moduleLabel}] ${typeLabel}`;

      // Build content with priority if applicable
      let fullContent = feedback;
      if (priority && (feedbackType === 'bug' || feedbackType === 'usability')) {
        const priorityLabel = priorities.find(p => p.id === priority)?.label || priority;
        fullContent = `**Impact Level:** ${priorityLabel}\n\n${feedback}`;
      }
      if (wantsFollowup && email) {
        fullContent += `\n\n---\n**Contact for follow-up:** ${email}`;
      }

      const result = await submitFeedback({
        type: selectedType?.backendType || 'general',
        title,
        content: fullContent,
        featureArea: selectedModule || undefined,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });

      if (result.data.success) {
        setIsSubmitted(true);
      } else {
        setError(result.data.error || 'Failed to submit feedback');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedModule(null);
    setFeedbackType(null);
    setPriority(null);
    setFeedback('');
    setEmail('');
    setWantsFollowup(false);
    setIsSubmitted(false);
    setError(null);
  };

  const canSubmit = selectedModule && feedbackType && feedback.trim().length > 10;

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex bg-airy">
        {/* Desktop Sidebar */}
        <Sidebar className="hidden lg:flex h-screen sticky top-0" activeCaseFileId={activeCaseFileId} />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen">
          <Header onMenuClick={() => setIsMobileSidebarOpen(true)} />

          <main className="flex-1 flex items-center justify-center p-6">
            <div className="card p-8 max-w-md text-center">
              <div className="w-20 h-20 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-evergreen" />
              </div>

              <h2 className="font-display text-2xl text-deep-forest mb-4">
                Thank you
              </h2>

              <p className="text-secondary-text mb-6 leading-relaxed">
                Your feedback helps us build a better experience for caregivers.
                {wantsFollowup && " We'll be in touch if we have questions."}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleReset}
                  className="btn-ghost"
                >
                  Submit More Feedback
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn-primary"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-airy">
      {/* Desktop Sidebar */}
      <Sidebar className="hidden lg:flex h-screen sticky top-0" activeCaseFileId={activeCaseFileId} />

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          <Sidebar
            className="relative h-full w-64"
            activeCaseFileId={activeCaseFileId}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <Header onMenuClick={() => setIsMobileSidebarOpen(true)} />

        <main className="flex-1 p-4 lg:p-6">
          {/* Page Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted hover:text-ink transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back</span>
            </button>

            <div className="flex items-center gap-3 mb-2">
              <span className="badge badge-brass text-xs font-semibold uppercase tracking-wide">
                Beta
              </span>
              <h1 className="font-display text-2xl text-deep-forest">
                Share Your Feedback
              </h1>
            </div>
            <p className="text-secondary-text">
              Help us improve Hesvara. Every piece of feedback shapes what we build next.
            </p>
          </div>

          {/* Form Card */}
          <div className="card p-6 max-w-3xl">
            <form onSubmit={handleSubmit}>

              {/* Error Message */}
              {error && (
                <div className="alert alert-error mb-6">
                  {error}
                </div>
              )}

              {/* Module Selection */}
              <section className="mb-8">
                <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-evergreen text-white rounded-full inline-flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  Which area is this about?
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {modules.map(module => {
                    const Icon = module.icon;
                    return (
                      <button
                        key={module.id}
                        type="button"
                        onClick={() => setSelectedModule(module.id)}
                        className={`
                          p-4 rounded-xl border-2 text-center transition-all
                          ${selectedModule === module.id
                            ? 'bg-evergreen text-white border-evergreen'
                            : 'bg-white text-ink border-soft-neutral hover:border-evergreen/50'
                          }
                        `}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${selectedModule === module.id ? 'bg-white/20' : module.color}`}>
                          <Icon className={`w-5 h-5 ${selectedModule === module.id ? 'text-white' : ''}`} />
                        </div>
                        <div className="font-semibold text-sm mb-1">{module.name}</div>
                        <div className={`text-xs ${selectedModule === module.id ? 'text-white/80' : 'text-secondary-text'}`}>
                          {module.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Feedback Type */}
              <section className="mb-8">
                <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold ${selectedModule ? 'bg-evergreen text-white' : 'bg-soft-neutral text-secondary-text'}`}>
                    2
                  </span>
                  What type of feedback?
                </h2>

                <div className="flex flex-wrap gap-2">
                  {feedbackTypes.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFeedbackType(type.id)}
                        className={`
                          px-4 py-2 rounded-full border-2 text-sm transition-all
                          flex items-center gap-2
                          ${feedbackType === type.id
                            ? 'bg-evergreen/10 text-evergreen border-evergreen font-semibold'
                            : 'bg-white text-ink border-soft-neutral hover:border-evergreen/50'
                          }
                        `}
                      >
                        <Icon className="w-4 h-4" />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Priority (only for bugs/usability) */}
              {(feedbackType === 'bug' || feedbackType === 'usability') && (
                <section className="mb-8">
                  <h2 className="text-sm font-semibold text-ink mb-3">
                    How much does this impact you?
                  </h2>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    {priorities.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPriority(p.id)}
                        className={`
                          p-3 rounded-lg border-2 text-left transition-all
                          ${priority === p.id
                            ? 'bg-slate-ink text-white border-slate-ink'
                            : 'bg-white text-ink border-soft-neutral hover:border-slate-ink/50'
                          }
                        `}
                      >
                        <div className="font-semibold text-sm mb-1">{p.label}</div>
                        <div className={`text-xs ${priority === p.id ? 'text-white/80' : 'text-secondary-text'}`}>
                          {p.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Feedback Text */}
              <section className="mb-8">
                <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold ${feedbackType ? 'bg-evergreen text-white' : 'bg-soft-neutral text-secondary-text'}`}>
                    3
                  </span>
                  Tell us more
                </h2>

                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={
                    feedbackType === 'bug'
                      ? "What happened? What did you expect to happen? Any steps to reproduce?"
                      : feedbackType === 'feature_request'
                      ? "What would you like to see? How would it help your caregiving work?"
                      : feedbackType === 'praise'
                      ? "What's working well? What made you smile?"
                      : "Share your thoughts..."
                  }
                  className="input-field w-full min-h-[160px] resize-y"
                />

                <div className="flex justify-between mt-2 text-sm text-secondary-text">
                  <span>Be as specific as you can - details help!</span>
                  <span>{feedback.length} characters</span>
                </div>
              </section>

              {/* Follow-up Option */}
              <section className="mb-8 p-4 bg-white rounded-xl border border-soft-neutral">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantsFollowup}
                    onChange={(e) => setWantsFollowup(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-evergreen"
                  />
                  <div>
                    <div className="font-semibold text-ink mb-1">
                      I'd like to hear back about this
                    </div>
                    <div className="text-sm text-secondary-text">
                      We may reach out to learn more or share updates
                    </div>
                  </div>
                </label>

                {wantsFollowup && (
                  <div className="mt-4 ml-8">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="input-field w-full max-w-xs"
                    />
                  </div>
                )}
              </section>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-soft-neutral">
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className={`
                    btn-primary flex items-center gap-2
                    ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center py-8 text-secondary-text text-sm">
            <p className="mb-2">
              🔒 Your feedback is confidential and used only to improve Hesvara.
            </p>
            <p>
              Questions? Email us at{' '}
              <a href="mailto:feedback@hesvara.com" className="text-slate-ink hover:underline">
                feedback@hesvara.com
              </a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

export default BetaFeedbackPage;
